import { defineStore, storeToRefs } from "pinia";
import { useToast } from "primevue/usetoast";
import { computed, ref } from "vue";

import { isHttpExceptionModel, type types, useServices } from "@/lib/openapi";
import { ClusterRole } from "@/stores/user-store";

export type OrganizationUser = types.OrganizationUser;

export const useUsersStore = defineStore("users", () => {
    const toast = useToast();
    const users = ref<OrganizationUser[]>([]);
    const userRoles = ref<Record<string, types.DomainRole[]>>({});
    const isLoading = ref(false);
    const currentError = ref<string | null>(null);

    const api = useServices({
        opts: { throwOnError: false },
        requires: {
            auth: true,
            organization: true,
            stek: false,
        },
    });

    /**
     * Get all users for the current organization
     */
    const getAllUsers = async () => {
        isLoading.value = true;
        currentError.value = null;

        try {
            const { response, data } = await api.getUsersUserGet({});

            if (!response?.ok) {
                currentError.value = "Failed to load users";
                return [];
            }

            users.value = data || [];
            return data;
        } catch (error) {
            handleError(error, "Failed to load users");
            return [];
        } finally {
            isLoading.value = false;
        }
    };

    /**
     * Get user domain roles for a specific user
     */
    const getUserRoles = async (userId: string) => {
        isLoading.value = true;
        currentError.value = null;

        try {
            const { response, data } =
                await api.getDomainRolesUserUserIdDomainRolesGet({
                    path: {
                        user_id: userId,
                    },
                });

            if (!response?.ok) {
                currentError.value = "Failed to load user roles";
                return [];
            }

            userRoles.value[userId] = data || [];
            return data;
        } catch (error) {
            handleError(error, "Failed to load user roles");
            return [];
        } finally {
            isLoading.value = false;
        }
    };

    /**
     * Add a user to the current organization
     */
    const addUserToOrganization = async (
        email: string,
        roles: ClusterRole[],
        name?: string
    ) => {
        isLoading.value = true;
        currentError.value = null;
        const defaultErrorMessage = "Failed to add user to organization";

        try {
            const { response, data } = await api.createUserUserPost({
                body: {
                    email,
                    name: name || email.split("@")[0],
                },
                throwOnError: true,
            });

            if (!response?.ok || !data) {
                handleError(null, defaultErrorMessage);
                currentError.value = defaultErrorMessage;
                return false;
            }

            // If the role is not the default USER, update to the requested roles.
            const isDefaultUser =
                roles.length === 1 && roles[0] === ClusterRole.USER;
            if (!isDefaultUser) {
                await updateUserRoles(data.identity, roles);
            } else {
                // Refresh the user list after adding a new user with the default role
                await getAllUsers();
            }

            toast.add({
                severity: "success",
                summary: "User added successfully",
                life: 3000,
            });

            return true;
        } catch (error) {
            handleError(error, defaultErrorMessage);
            return false;
        } finally {
            isLoading.value = false;
        }
    };

    /**
     * Remove a user from the current organization
     */
    const removeUserFromOrganization = async (userId: string) => {
        isLoading.value = true;
        currentError.value = null;

        try {
            const { response } = await api.deleteUserUserUserIdDelete({
                path: {
                    user_id: userId,
                },
            });

            if (!response?.ok) {
                currentError.value = "Failed to remove user from organization";
                return false;
            }

            // Remove user from local state
            users.value = users.value.filter(
                (user) => user.identity !== userId
            );
            delete userRoles.value[userId];

            toast.add({
                severity: "success",
                summary: "User removed successfully",
                life: 3000,
            });

            return true;
        } catch (error) {
            handleError(error, "Failed to remove user from organization");
            return false;
        } finally {
            isLoading.value = false;
        }
    };

    /**
     * Update roles for a user in the organization using the multi-role endpoint.
     */
    const updateUserRoles = async (userId: string, roles: ClusterRole[]) => {
        isLoading.value = true;
        currentError.value = null;

        try {
            const { response, data } =
                await api.updateAuthorizationUserUserIdAuthorizationPut({
                    path: { user_id: userId },
                    body: { cluster_roles: roles },
                });

            if (!response?.ok || !data) {
                currentError.value = "Failed to update user roles";
                return false;
            }

            const userIndex = users.value.findIndex(
                (user) => user.identity === userId
            );
            if (userIndex !== -1) {
                users.value[userIndex] = data;
            } else {
                await getAllUsers();
            }

            return true;
        } catch (error) {
            handleError(error, "Failed to update user roles");
            return false;
        } finally {
            isLoading.value = false;
        }
    };

    /**
     * Update a user's authorization level in the organization (single-role, backward-compat).
     */
    const updateUserAuthorizationLevel = async (
        userId: string,
        role: ClusterRole
    ) => {
        const success = await updateUserRoles(userId, [role]);

        if (success) {
            toast.add({
                severity: "success",
                summary: "User role updated successfully",
                life: 3000,
            });
        }

        return success;
    };

    /**
     * Helper function to handle API errors
     */
    const handleError = (error: unknown, defaultMessage: string) => {
        if (isHttpExceptionModel(error)) {
            const serverMessage = error.detail[0]?.message || defaultMessage;
            currentError.value = serverMessage;
            toast.add({
                severity: "error",
                summary: serverMessage,
                life: 5000,
            });
        } else {
            currentError.value = defaultMessage;
            toast.add({
                severity: "error",
                summary: defaultMessage,
                life: 5000,
            });
        }
        console.error(error);
    };

    return {
        users,
        userRoles,
        isLoading,
        currentError,
        getAllUsers,
        getUserRoles,
        addUserToOrganization,
        removeUserFromOrganization,
        updateUserRoles,
        updateUserAuthorizationLevel,
        hasUsers: computed(() => users.value.length > 0),
    };
});

export const useUsersManager = () => storeToRefs(useUsersStore());
